import React, { useState } from 'react';
import { Collapse } from '@mui/material';

// SERVICES
import { packingTrackingHistoryApi, shippingCompanyApi } from '@/main/api';
import { getTranslateByKey } from '@/main/services/utils';
import {
  GetByIdPackingTrackingResponse,
  GetShippingCompaniesResponse
} from '@/main/models/response/packingTrackingHistoryResponse';
import { PartResponse } from '@/main/models/response';
import { userIsAdmin, userIsSupplier } from '@/main/services/utilsUser';

//SCREENS/COMPONENTS/STYLES LOCAL
import PartComponent from './partComponent';
import SeeShippingDetails from '../seeShippingDetails/seeShippingDetails';
import * as S from './styles';
import { IMAGE_DEFAULT } from '@/main/services/constants';
import SeeShippingCompanies from '../seeShippingCompanies/seeShippingCompanies';
import { RoutesConstants } from '@/main/routes/routesConstants';

interface IPack {
  packNumber: number;
  supplier: string;
  trackingNumber: number;
  guidPackingTrackingDelivery: string;
  guidProcurementSupplier: string;
  logisticCompanyName: string;
  shippingCompanyLogo: string;
  guidPackingTrackingReturn?: string;
  trackingCodeReturn?: number;
  height: number;
  width: number;
  depth: number;
  orderNumber: string | null;
  collectionDate: Date;
  showShippingCompanyAddress: boolean;
}

interface IListPartSupplier {
  parts: PartResponse[];
  pack: IPack;
}

function ListPartSupplier({ parts, pack }: IListPartSupplier) {
  const [dataSeeShippingDetails, setDataSeeShippingDetails] = useState<
    GetByIdPackingTrackingResponse[]
  >([]);
  const [
    isLoadingDataSeeShippingDetails,
    setIsLoadingDataSeeShippingDetails
  ] = useState<boolean>(false);
  const [seeShippingDetails, setSeeShippingDetails] = useState<boolean>(false);
  const [seeMoreItems, setSeeMoreItems] = useState<boolean>(false);
  const [seeShippingCompanies, setSeeShippingCompanies] = useState<boolean>(
    false
  );
  const [dataSeeShippingCompanies, setDataSeeShippingCompanies] = useState<
    GetShippingCompaniesResponse[]
  >([]);
  const [
    isLoadingDataSeeShippingCompanies,
    setIsLoadingDataSeeShippingCompanies
  ] = useState<boolean>(false);

  // Constants
  const isDevolution = () =>
    pack.guidPackingTrackingReturn && pack.guidPackingTrackingReturn != '';
  const isUserAdmin = userIsAdmin();
  const isUserSupplier = userIsSupplier();
  const firstItems = parts.slice(0, 1);
  const restOfItems = parts.slice(1);
  const showMoreButtonSeeMoreItems = 2;
  const packTrackingNumber = isDevolution()
    ? pack.trackingCodeReturn
    : pack.trackingNumber;

  function handleChangeShippingDetails() {
    setDataSeeShippingDetails([]);
    setSeeShippingDetails(prev => !prev);
    setSeeShippingCompanies(false);
    if (!seeShippingDetails) {
      setIsLoadingDataSeeShippingDetails(true);
      setSeeMoreItems(false);
      const request = isDevolution()
        ? pack.guidPackingTrackingReturn
        : pack.guidPackingTrackingDelivery;
      packingTrackingHistoryApi
        .get(request)
        .then(res => {
          const resData = res.data.result;
          setDataSeeShippingDetails(resData);
        })
        .catch(() => { })
        .finally(() => setIsLoadingDataSeeShippingDetails(false));
    }
  }

  function handleChangeSeeMoreItems() {
    setSeeMoreItems(prev => !prev);
    setSeeShippingCompanies(false);
  }

  function handleChangeSeeShippingCompanies() {
    setSeeShippingDetails(false);
    setSeeMoreItems(false);
    const seeShippingCompaniesChanged = !seeShippingCompanies;
    setSeeShippingCompanies(seeShippingCompaniesChanged);

    if (seeShippingCompaniesChanged) {
      setIsLoadingDataSeeShippingCompanies(true);
      shippingCompanyApi
        .getShippingCompany(
          pack.guidProcurementSupplier,
          pack.guidPackingTrackingDelivery
        )
        .then(res => {
          setDataSeeShippingCompanies(res.data.result);
        })
        .catch(() => { })
        .finally(() => setIsLoadingDataSeeShippingCompanies(false));
    }
  }

  function showButtonshowShippingCompanyAddress() {
    if (isUserAdmin || isUserSupplier) {
      if (pack.showShippingCompanyAddress) {
        return true;
      }
    }

    return false;
  }

  function renderPartComponent(part: PartResponse) {
    return (
      <S.BoxGridParts key={part.partGuid}>
        <PartComponent
          image={part.image1 || IMAGE_DEFAULT}
          quantity={part.quantityPart}
          quoteProcurementPartInfo={part.quoteProcurementPartInfo}
        />
      </S.BoxGridParts>
    );
  }

  function renderPackageInformationHeader(pack: IPack) {
    let packageType;

    if (pack.height)
      packageType = (
        <React.Fragment>
          {pack.height}
          &nbsp;x&nbsp;
          {pack.width}
          &nbsp;x&nbsp;
          {pack.depth}
          &nbsp; - &nbsp;
        </React.Fragment>
      );

    return (
      <React.Fragment>
        {getTranslateByKey('information.package')}
        &nbsp;
        {pack.packNumber}
        &nbsp; - &nbsp;
        {packageType}
        {pack.supplier}
      </React.Fragment>
    );
  }

  const initialDateCollection = new Date(pack.collectionDate);
  initialDateCollection.setDate(initialDateCollection.getDate() - 1);
  return (
    <S.Content>
      {isDevolution() && (
        <S.TextDevolution>
          {getTranslateByKey('information.returned')}
        </S.TextDevolution>
      )}
      <S.BoxTextPackTracking>
        <strong>{renderPackageInformationHeader(pack)}</strong>
        <span>
          {getTranslateByKey('packingTrackingHistory.trackingNumber')}:
          <S.HyperLink
            href={`/#${RoutesConstants.orderManagePackages}?quoteFilterVehicle=${pack.orderNumber}&startDate=${initialDateCollection}&finalDate=${pack.collectionDate}`}
            target="_blank">
            {packTrackingNumber}
          </S.HyperLink>
        </span>
      </S.BoxTextPackTracking>

      <Collapse in={!seeShippingDetails && !seeShippingCompanies}>
        <S.ContentListsPart>
          {firstItems.map(part => {
            return renderPartComponent(part);
          })}
          <Collapse in={seeMoreItems}>
            <S.ContentListsPart>
              {restOfItems.map(part => {
                return renderPartComponent(part);
              })}
            </S.ContentListsPart>
          </Collapse>
        </S.ContentListsPart>
      </Collapse>

      <SeeShippingDetails
        isLoadingDataSeeShippingDetails={isLoadingDataSeeShippingDetails}
        dataSeeShippingDetails={dataSeeShippingDetails}
        checked={seeShippingDetails}
        trackingNumber={String(pack.trackingNumber)}
      />

      <SeeShippingCompanies
        isLoadingDataSeeShippingCompanies={isLoadingDataSeeShippingCompanies}
        dataSeeShippingCompanies={dataSeeShippingCompanies}
        checked={seeShippingCompanies}
        shippingCompanyLogo={pack.shippingCompanyLogo}
      />

      <S.ContainerButton>
        <S.TextButtonOpen type="button" onClick={handleChangeShippingDetails}>
          {seeShippingDetails
            ? getTranslateByKey('packingTrackingHistory.closeShippingDetails')
            : getTranslateByKey('packingTrackingHistory.seeShippingDetails')}
        </S.TextButtonOpen>

        {seeShippingDetails === false &&
          parts.length >= showMoreButtonSeeMoreItems && (
            <S.TextButtonOpen type="button" onClick={handleChangeSeeMoreItems}>
              {seeMoreItems
                ? getTranslateByKey('packingTrackingHistory.hideItems')
                : getTranslateByKey('packingTrackingHistory.seeMoreItems')}
            </S.TextButtonOpen>
          )}

        {showButtonshowShippingCompanyAddress() && (
          <S.TextButtonOpen
            type="button"
            onClick={handleChangeSeeShippingCompanies}>
            {seeShippingCompanies
              ? getTranslateByKey('information.hideShippingCompaniesNearYou')
              : getTranslateByKey('information.seeShippingCompaniesNearYou')}
          </S.TextButtonOpen>
        )}
      </S.ContainerButton>
    </S.Content>
  );
}

export default ListPartSupplier;
